import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, MicOff } from 'lucide-react'
import { startVoiceControl, stopVoiceControl } from '../lib/voiceControl'

type State = 'off' | 'starting' | 'listening' | 'stopping'

export default function VoiceControl() {
  const navigate = useNavigate()
  const [state, setState] = useState<State>('off')
  const [error, setError] = useState<string | null>(null)
  const attempt = useRef(0)
  const busy = useRef(false)

  useEffect(() => {
    function stop() {
      attempt.current += 1
      busy.current = false
      setState('off')
      void stopVoiceControl().catch(() => setError('Could not stop the microphone. Close AgentChat and reopen it.'))
    }
    function hidden() {
      if (document.visibilityState !== 'visible') stop()
    }
    document.addEventListener('visibilitychange', hidden)
    window.addEventListener('pagehide', stop)
    return () => {
      attempt.current += 1
      document.removeEventListener('visibilitychange', hidden)
      window.removeEventListener('pagehide', stop)
      void stopVoiceControl().catch(() => undefined)
    }
  }, [])

  async function toggle() {
    if (state === 'stopping' || (state === 'off' && busy.current)) return
    busy.current = true
    const id = ++attempt.current
    setError(null)
    if (state !== 'off') {
      setState('stopping')
      try { await stopVoiceControl() } catch {
        if (attempt.current === id) setError('Could not stop the microphone. Close AgentChat and reopen it.')
      } finally {
        if (attempt.current === id) { setState('off'); busy.current = false }
      }
      return
    }
    setState('starting')
    try {
      await startVoiceControl({
        onCommand: () => {
          if (attempt.current !== id || document.visibilityState !== 'visible') return
          attempt.current += 1
          busy.current = false
          setState('off')
          navigate(`/code/new/codex?launch_id=${crypto.randomUUID()}`)
        },
        onError: (message) => {
          if (attempt.current !== id) return
          attempt.current += 1
          busy.current = false
          setState('off')
          setError(message)
        },
      })
      if (attempt.current === id) { setState('listening'); busy.current = false }
    } catch (failure) {
      if (attempt.current !== id) return
      setState('off')
      busy.current = false
      setError(failure instanceof Error ? failure.message : 'Could not start voice control')
    }
  }

  const active = state !== 'off'
  return <div className="relative">
    <button type="button" onClick={() => void toggle()}
      disabled={state === 'stopping'}
      aria-label={active ? 'Stop voice control' : 'Start voice control'} aria-pressed={active}
      title='Voice control: say "Hey AgentChat, new Codex chat"'
      className={`grid h-9 w-9 place-items-center rounded-lg ${active ? 'bg-blue-600 text-white' : 'hover:bg-stone-200 dark:hover:bg-stone-800 text-stone-500'}`}>
      {active ? <MicOff className="h-4 w-4" aria-hidden /> : <Mic className="h-4 w-4" aria-hidden />}
    </button>
    {(active || error) && <div className="absolute top-full right-0 z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-stone-200 bg-stone-50 p-3 shadow-lg dark:border-stone-700 dark:bg-stone-900">
      {error ? <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p> : <>
        <p role="status" className="text-sm font-medium">{state === 'starting' ? 'Starting microphone...' : state === 'stopping' ? 'Stopping microphone...' : 'Listening for a new chat'}</p>
        <p className="mt-1 text-sm">Say “Hey AgentChat, new Codex chat”.</p>
        <p className="mt-2 text-xs text-stone-500">Keep AgentChat open. Listening stops after two minutes or when you leave this screen. Speech uses your device or browser recognition service.</p>
      </>}
      <button type="button" className="mt-2 min-h-9 text-sm underline" onClick={() => { if (active) void toggle(); else setError(null) }}>
        {active ? 'Stop listening' : 'Dismiss'}
      </button>
    </div>}
  </div>
}
